import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  Spinner,
  Dropdown,
  DropdownSearchToggle,
  DropdownMenu,
  DropdownItem,
  Button,
  Heading,
  TextField
} from '@telia/styleguide';

import { useFWAOrder } from '../../hooks/FWAOrderContext';
import { AddressLookup } from '../../store/actions';
import { MediaQueries } from '../../util/MediaQueries';

type CheckAvailability = {
  checkAvailability: (address: AddressLookup) => void;
};
const SearchInputWrapper = styled.div`
  margin: 0 1rem 1rem 0;
  display: flex;
  flex-direction: row;
  align-items: center;

  ${MediaQueries.onlyMobile} {
    flex-direction: column;
    margin-right: auto;
  }
`;
const PostalRegion = styled.div`
  transform: translateY(50%);
  margin-left: 1rem;
  ${MediaQueries.onlyMobile} {
    transform: none;
    margin: 0;
  }
`;

const SearchWrapper = styled.div`
  box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.25);
  padding: 2rem 6rem;
  display: flex;
  flex-direction: column;
  ${MediaQueries.onlyMobile} {
    width: 100%;
    padding: 2rem 1rem;
  }
`;

const ZipCode = () => {
  const { addressSearch } = useFWAOrder();
  const { zipCodeData, formState, setInputValue } = addressSearch;
  const { region, loading, error } = zipCodeData;

  return (
    <SearchInputWrapper>
      <TextField
        error={error}
        helpText={error ? 'Kunne ikke hente poststed' : ''}
        label="Postnummer"
        placeholder="Postnummer"
        name="postnummer"
        onChange={e => {
          if (e.target.value.length + 1 > 5) {
            return;
          }
          setInputValue('zipCode')(e.target.value);
        }}
        value={formState.zipCode.inputValue}
      />

      <PostalRegion>{loading ? <Spinner type="sm" /> : region}</PostalRegion>
    </SearchInputWrapper>
  );
};

const StreetName = () => {
  const { addressSearch } = useFWAOrder();
  const { streetNames = [], formState, setInputValue, setSelectedValue } = addressSearch;

  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (streetNames?.length > 1 && !open) {
      setOpen(true);
    }
  }, [streetNames]);

  return (
    <SearchInputWrapper>
      <Dropdown fullWidth>
        <DropdownSearchToggle
          label="Gateadresse"
          onInputChange={setInputValue('streetName')}
          value={formState.streetName.inputValue}
          openImmediately
          disabled={!formState.zipCode.selectedValue}
        />
        <DropdownMenu position="bottom-right">
          <div style={{ maxHeight: '300px', overflow: 'auto' }}>
            {streetNames?.map((street, idx) => (
              <DropdownItem
                key={`${street}-${idx}`}
                label={street}
                onClick={() => {
                  setSelectedValue('streetName')(street);
                  setOpen(false);
                }}
              />
            ))}
          </div>
        </DropdownMenu>
      </Dropdown>
    </SearchInputWrapper>
  );
};

const StreetNumber = () => {
  const { addressSearch } = useFWAOrder();
  const { streetNumbers, formState, setInputValue, setSelectedValue } = addressSearch;

  return (
    <SearchInputWrapper>
      <Dropdown fullWidth>
        <DropdownSearchToggle
          label="Nr"
          onInputChange={setInputValue('streetNumber')}
          value={formState.streetNumber.inputValue}
          openImmediately={true}
          disabled={!formState.streetName.selectedValue}
        />
        <DropdownMenu position="bottom-right">
          <div style={{ maxHeight: '300px', overflow: 'auto' }}>
            {streetNumbers?.map(streetNumber => (
              <DropdownItem
                key={streetNumber}
                label={streetNumber}
                onClick={() => {
                  setSelectedValue('streetNumber')(streetNumber);
                }}
              />
            ))}
          </div>
        </DropdownMenu>
      </Dropdown>
    </SearchInputWrapper>
  );
};

const StreetNumberFloor = () => {
  const { addressSearch } = useFWAOrder();
  const { floors, formState, setInputValue, setSelectedValue, oneFloorOneUnit } = addressSearch;

  if (!formState.streetNumber.selectedValue || oneFloorOneUnit) {
    return null;
  }

  return (
    <SearchInputWrapper>
      <Dropdown fullWidth>
        <DropdownSearchToggle
          label="Etasje"
          onInputChange={setInputValue('floor')}
          value={formState.floor.inputValue}
          openImmediately={true}
          disabled={!formState.streetNumber.selectedValue}
        />
        <DropdownMenu position="bottom-right">
          <div style={{ maxHeight: '300px', overflow: 'auto' }}>
            {floors?.map(f => (
              <DropdownItem
                key={f}
                label={f}
                onClick={() => {
                  setSelectedValue('floor')(f);
                }}
              />
            ))}
          </div>
        </DropdownMenu>
      </Dropdown>
    </SearchInputWrapper>
  );
};

const ApartmentNumber = () => {
  const { addressSearch } = useFWAOrder();
  const {
    formState,
    apartments,
    setInputValue,
    setSelectedValue,
    oneFloorOneUnit,
    oneUnitInSelectedFloor
  } = addressSearch;

  if (!formState.streetNumber.selectedValue || oneFloorOneUnit || oneUnitInSelectedFloor) {
    return null;
  }

  return (
    <SearchInputWrapper>
      <Dropdown fullWidth>
        <DropdownSearchToggle
          label="Leilighet"
          onInputChange={setInputValue('apartment')}
          value={formState.apartment.inputValue}
          openImmediately={true}
          disabled={!formState.floor.selectedValue}
        />
        <DropdownMenu position="bottom-right">
          <div style={{ maxHeight: '300px', overflow: 'auto' }}>
            {apartments?.map(a => (
              <DropdownItem
                key={a}
                label={a}
                onClick={() => {
                  setSelectedValue('apartment')(a);
                }}
              />
            ))}
          </div>
        </DropdownMenu>
      </Dropdown>
    </SearchInputWrapper>
  );
};

const CheckAvailability = ({ checkAvailability }: CheckAvailability) => {
  const { addressSearch } = useFWAOrder();
  const { validAddress } = addressSearch;

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Button
        autoFocus={true}
        onClick={() => {
          if (validAddress) {
            checkAvailability(validAddress);
          }
        }}
        style={{ marginTop: '2.8rem' }}
        text="Sjekk"
        isDisabled={!validAddress}
        disabled={!validAddress}
      />
    </div>
  );
};

export const StreetNameSearch = ({ checkAvailability }: CheckAvailability) => {
  return (
    <SearchWrapper>
      <Heading tag="h1" size="xs" text="Sjekk om du kan få Trådløst bredbånd" style={{ color: '#222' }} />

      <ZipCode />
      <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
        <StreetName />
        <StreetNumber />
      </div>
      <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
        <StreetNumberFloor />
        <ApartmentNumber />
      </div>
      <CheckAvailability checkAvailability={checkAvailability} />
    </SearchWrapper>
  );
};
